import { Suspense, lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { HomeSwitch } from './HomeSwitch';
import { authRoutes } from './authRouter';

import { Root } from '@/Root';
import { ReportsLobby } from '@/components/Lobbys/Reports.lobby';
import { CampaignsLobby } from '@/components/Lobbys/Campaigns.lobby';

const Support = lazy(() => import('@/microfrontends/iframe/support.mfe'));
const LiveMfe = lazy(() => import('@/microfrontends/live.mfe'));
const AccountMfe = lazy(() => import('@/microfrontends/Account.mfe'));
const BillingMFE = lazy(() => import('@/microfrontends/iframe/billing.mfe'));
const ReportsChatbotMicrofrontend = lazy(
  () => import('@/microfrontends/reports')
);
const ReportsAIMicrofrontend = lazy(
  () => import('@/microfrontends/reports/reports.ai')
);
const HSMIframe = lazy(() => import('@/microfrontends/hsm-iframe'));
const GeniaChatbotModule = lazy(
  () => import('@/microfrontends/genia-chatbot-module')
);
const CampaignsMicrofrontend = lazy(() => import('@/microfrontends/campaigns'));
const DashboardMicrofrontend = lazy(
  () => import('@/microfrontends/dashboard.mfe')
);
const AffiliatesMicrofrontend = lazy(
  () => import('@/microfrontends/Affiliates.mfe')
);
const SubscriptionsMicrofrontend = lazy(
  () => import('@/microfrontends/subscriptions.mfe')
);

export const router: RouteObject[] = [
  ...(authRoutes as RouteObject[]),
  {
    path: '/',
    element: <Root />,
    children: [
      {
        path: '/',
        element: <HomeSwitch />,
      },
      {
        path: '/home',
        element: (
          <Suspense fallback={<p>...</p>}>
            <DashboardMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/reports/reports-chatbot',
        element: (
          <Suspense fallback={<p>...</p>}>
            <ReportsChatbotMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/reports/reports-ai',
        element: (
          <Suspense fallback={<p>...</p>}>
            <ReportsAIMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/chatbot/*',
        element: (
          <Suspense fallback={<p>...</p>}>
            <GeniaChatbotModule />
          </Suspense>
        ),
      },
      {
        path: '/kanban-channel/*',
        element: (
          <Suspense fallback={<p>...</p>}>
            <GeniaChatbotModule />
          </Suspense>
        ),
      },
      {
        path: '/campaigns',
        element: <CampaignsLobby />,
      },
      {
        path: '/campaigns/hsm',
        element: (
          <Suspense fallback={<p>...</p>}>
            <HSMIframe />
          </Suspense>
        ),
      },
      {
        path: '/campaigns/:companyId',
        element: (
          <Suspense fallback={<p>...</p>}>
            <CampaignsMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/live',
        element: (
          <Suspense fallback={<p>...</p>}>
            <LiveMfe initialPath="/live/" />
          </Suspense>
        ),
      },
      {
        path: '/live/channels',
        element: (
          <Suspense fallback={<p>...</p>}>
            <LiveMfe initialPath="/live/" />
          </Suspense>
        ),
      },
      {
        path: '/live/channels/*',
        element: (
          <Suspense fallback={<p>...</p>}>
            <LiveMfe initialPath="/live/" />
          </Suspense>
        ),
      },
      {
        path: '/live/history',
        element: (
          <Suspense fallback={<p>...</p>}>
            <LiveMfe initialPath="/live/" />
          </Suspense>
        ),
      },
      {
        path: '/account/plans',
        element: (
          <Suspense fallback={<p>...</p>}>
            <SubscriptionsMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/account/*',
        element: (
          <Suspense fallback={<p>...</p>}>
            <AccountMfe />
          </Suspense>
        ),
      },
      {
        path: '/account/billing',
        element: (
          <Suspense fallback={<p>...</p>}>
            <BillingMFE />
          </Suspense>
        ),
      },
      {
        path: '/integrations',
        element: <div id="crm-outlet" />,
      },
      {
        path: '/support',
        element: (
          <Suspense fallback={<p>...</p>}>
            <Support />
          </Suspense>
        ),
      },
      {
        path: '/reports',
        element: <ReportsLobby />,
      },
      {
        path: '/affiliates',
        element: (
          <Suspense>
            <AffiliatesMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/affiliates/commissions/:commissionType',
        element: (
          <Suspense>
            <AffiliatesMicrofrontend />
          </Suspense>
        ),
      },
    ],
  },
];

export const freeTrialEndedRouter: RouteObject[] = [
  {
    path: '/',
    element: <Root />,
    children: [
      {
        index: true,
        element: <Navigate to="/account/plans" replace />,
      },
      {
        path: '/support',
        element: (
          <Suspense fallback={<p>...</p>}>
            <Support />
          </Suspense>
        ),
      },
      {
        path: '/account/plans',
        element: (
          <Suspense fallback={<p>...</p>}>
            <SubscriptionsMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/affiliates',
        element: (
          <Suspense>
            <AffiliatesMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/affiliates/commissions/:commissionType',
        element: (
          <Suspense>
            <AffiliatesMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/account/*',
        element: <Navigate to="/account/plans" replace />,
      },
      {
        path: '*',
        element: <Navigate to="/account/plans" replace />,
      },
    ],
  },
];

export const userHasDebtsRouter: RouteObject[] = [
  {
    path: '/',
    element: <Root />,
    children: [
      {
        index: true,
        element: <Navigate to="/account/billing" replace />,
      },
      {
        path: '/support',
        element: (
          <Suspense fallback={<p>...</p>}>
            <Support />
          </Suspense>
        ),
      },
      {
        path: '/account/*',
        element: <Navigate to="/account/billing" replace />,
      },
      {
        path: '/account/billing',
        element: (
          <Suspense fallback={<p>...</p>}>
            <BillingMFE />
          </Suspense>
        ),
      },
      {
        path: '/affiliates',
        element: (
          <Suspense>
            <AffiliatesMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '/affiliates/commissions/:commissionType',
        element: (
          <Suspense>
            <AffiliatesMicrofrontend />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: <Navigate to="/account/billing" replace />,
      },
    ],
  },
];
